import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Workspace-embedded recurring-journals readiness card.
 *
 * Surfaces the controller's recurring-journals checklist for the period's
 * month-key directly on the Complete & Sign Off step. Read-only — actions
 * are performed on the Recurring Journals page itself.
 *
 * Outstanding items do not block completion (control register, not a
 * reconciliation blocker), so the card is purely informational.
 */
export default function RecurringJournalsReadinessCard({ periodMonthKey }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!periodMonthKey) return;
    axios
      .get(`${API}/recurring-journals/checklist`, { params: { month: periodMonthKey } })
      .then(r => setData(r.data))
      .catch(e => setErr(e.message));
  }, [periodMonthKey]);

  if (err) {
    return (
      <div className="card" data-testid="rj-readiness-card-error" style={{ marginTop: 16 }}>
        <div className="heading h3">Recurring Journals</div>
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Unable to load checklist: {err}</div>
      </div>
    );
  }
  if (!data) return null;

  const items = data.items || [];
  const counts = {
    expected: items.filter(i => i.computed_status === "expected" && i.applicable).length,
    recorded: items.filter(i => i.computed_status === "recorded").length,
    skipped: items.filter(i => i.computed_status === "skipped").length,
    na: items.filter(i => i.computed_status === "not_applicable").length,
  };
  const total_applicable = counts.expected + counts.recorded + counts.skipped + counts.na;
  const all_confirmed = counts.expected === 0;

  return (
    <div
      className="card"
      data-testid="rj-readiness-card"
      style={{ marginTop: 16 }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14 }}>
        <div>
          <div className="heading h3">Recurring Journals · {periodMonthKey}</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Have all expected recurring General Ledger adjustments been posted before certification?
          </div>
        </div>
        <Link
          to="/recurring-journals"
          data-testid="rj-readiness-open-page"
          className="btn btn-sm"
          style={{ whiteSpace: "nowrap" }}
        >
          Open page
        </Link>
      </div>

      {total_applicable === 0 ? (
        <div className="muted" data-testid="rj-readiness-none" style={{ fontSize: 12, marginTop: 12 }}>
          No active recurring templates apply to this period.
        </div>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 10,
              marginTop: 14,
            }}
          >
            <ReadinessTile
              testid="rj-readiness-expected"
              label="Pending verification"
              value={counts.expected}
              tone={counts.expected > 0 ? "warn" : "ok"}
            />
            <ReadinessTile testid="rj-readiness-recorded" label="Confirmed" value={counts.recorded} tone="ok" />
            <ReadinessTile testid="rj-readiness-skipped" label="Skipped" value={counts.skipped} tone="mute" />
            <ReadinessTile testid="rj-readiness-na" label="Not applicable" value={counts.na} tone="mute" />
          </div>

          <div
            data-testid="rj-readiness-banner"
            style={{
              marginTop: 14,
              padding: "10px 12px",
              borderRadius: 6,
              fontSize: 12,
              background: all_confirmed ? "var(--accent-soft)" : "var(--surface-elev)",
              color: all_confirmed ? "var(--accent)" : "var(--ink-mute)",
              border: "1px solid " + (all_confirmed ? "var(--accent)" : "var(--line)"),
            }}
          >
            {all_confirmed
              ? "All expected recurring General Ledger adjustments have been confirmed for this month."
              : `${counts.expected} expected recurring adjustment${counts.expected === 1 ? "" : "s"} still pending verification. ` +
                "Completion is not blocked — this register is informational."}
          </div>
        </>
      )}
    </div>
  );
}

const ReadinessTile = ({ testid, label, value, tone }) => (
  <div
    data-testid={testid}
    style={{
      padding: "10px 12px",
      borderRadius: 6,
      background: "var(--surface-elev)",
      border: "1px solid var(--line)",
    }}
  >
    <div
      style={{
        fontSize: 10,
        color: "var(--ink-mute)",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        fontWeight: 600,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: 22,
        fontWeight: 700,
        marginTop: 4,
        color:
          tone === "warn" && value > 0
            ? "var(--blocker)"
            : tone === "ok" && value > 0
            ? "var(--accent)"
            : "var(--ink)",
      }}
    >
      {value}
    </div>
  </div>
);
